/** One country: partners registered there and newsletter output over time.
 *
 * The per-year chart is also a control: picking a year re-ranks the prolific-partners
 * table to that year alone. Every year's ranking arrives with the page (`byYearPartner`),
 * so a pick is instant and the rest of the page does not blink. */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { BarsChart } from '../components/charts'
import { YearSelect } from '../components/Select'
import {
  Card, Tile, Badge, Empty, Loading, ErrorState, ActivityBadge, BackTo,
  fmtInt, fmtDate, relDays, AlumniSwitch, AlumniHidden,
} from '../components/ui'
import { useCode } from '../lib/terms'

/** Labelled the way the sidebar and the list's own header label the route. */
const Back = () => <BackTo to="/countries">Country / Category</BackTo>

/** As many partner units as the all-time ranking shows, so a year reads like it. */
const TOP_N = 10

export default function CountryDetail() {
  const code = useCode()
  const { id } = useParams()
  // The partner list, the per-year chart and the top-partners table are all this
  // country's current partner units unless the switch asks for the past ones too.
  const [withAlumni, setWithAlumni] = useState(false)
  const query = useApi(
    ['country', id, withAlumni],
    `countries/${id}${withAlumni ? '?include_alumni=1' : ''}`
  )
  const [year, setYear] = useState(null)

  /* A picked year belongs to the country it was picked on, the same way it does on a
     partner unit record: adjusted while rendering rather than in an effect, so the page
     is never painted showing one country's year over another's numbers. */
  const [seenId, setSeenId] = useState(id)
  if (seenId !== id) {
    setSeenId(id)
    setYear(null)
  }

  // Spelled out rather than left to QueryState, which renders its states bare: the
  // way back has to survive a record that is still loading or will not load, and a
  // broken id is exactly when the reader most needs it.
  if (query.isLoading) {
    return (
      <PageHeader title="Country" back={<Back />}>
        <Loading />
      </PageHeader>
    )
  }
  if (query.isError) {
    return (
      <PageHeader title="Country" back={<Back />}>
        <ErrorState error={query.error} retry={query.refetch} />
      </PageHeader>
    )
  }

  const d = query.data
  const c = d.country
  const years = (d.byYear ?? []).map((r) => String(r.year))
  /* The alumni switch changes what the chart counts, so a year can stop existing under
     it — picking 2011 and then hiding alumni, where they were the only ones writing that
     year, would leave a heading over an empty table and no bar to explain it. */
  const picked = year && years.includes(year) ? year : null
  /* Prolific partner units, all-time or in the picked year. Both come from the same
     rollup shape — partner label and a count — so one table renders either. */
  const ranked = picked
    ? (d.byYearPartner ?? [])
        .filter((r) => String(r.year) === picked)
        .sort((a, b) => b.n - a.n)
        .slice(0, TOP_N)
    : (d.topPartners ?? [])
  return (
    <PageHeader
      title={`${c.flag_emoji ? `${c.flag_emoji} ` : ''}${c.country_name}`}
      sub={
        <>
          <Badge>{c.kind}</Badge>
          <AlumniHidden count={d.alumniHidden} noun="partner units" />
        </>
      }
      back={<Back />}
      actions={
        <>
          <AlumniSwitch
            checked={withAlumni}
            onChange={setWithAlumni}
            where="partner units"
          />
          <Link className="btn btn-sm" to={`/newsletters?country_id=${c.country_id}`}>
            Browse updates
          </Link>
        </>
      }
    >
      <div className="col">
        <div className="grid grid-4">
          <Tile label="Partner units registered" value={fmtInt(c.partner_count)} />
          <Tile label="Updates" value={fmtInt(c.newsletter_count)} />
          <Tile label="Highlighted Updates" value={fmtInt(c.highlighted_count)} tone="warn" />
          <Tile label="Latest" value={fmtDate(c.last_newsletter)} note={relDays(c.last_newsletter)} />
        </div>

        {d.byYear?.length ? (
          <Card
            title="Updates per Year"
            sub="Counted by the country recorded on each update — pick a year to rank the partner units in it"
          >
            <BarsChart
              data={d.byYear}
              x="year"
              series={[{ key: 'n', label: 'Updates' }]}
              height={220}
              /* Picking the year already showing goes back to the all-time ranking, so
                 the bar behaves like the toggle it looks like. */
              onPick={(y) => setYear((cur) => (cur === y ? null : y))}
              activeX={picked}
            />
          </Card>
        ) : null}

        <div className="grid grid-2">
          <Card
            title={picked ? `Most Prolific Partner Units in ${picked}` : 'Most Prolific Partner Units Here'}
            sub={
              picked
                ? `${fmtInt(ranked.reduce((a, r) => a + r.n, 0))} of this country's ${fmtInt(
                    d.byYear.find((r) => String(r.year) === picked)?.n ?? 0
                  )} updates that year`
                : undefined
            }
            actions={
              years.length > 1 ? (
                /* The chart's selection as a control, since a click on an SVG is not
                   reachable from a keyboard and clearing it there is the only way back. */
                <div style={{ width: 140 }}>
                  <YearSelect
                    inputId="country-year"
                    years={years}
                    value={picked}
                    onChange={setYear}
                    placeholder="All years"
                  />
                </div>
              ) : null
            }
          >
            {ranked.length ? (
              <div className="table-wrap flush">
                <table className="data compact">
                  <tbody>
                    {ranked.map((p) => (
                      <tr key={p.partner_id ?? p.s4ca_code}>
                        <td className="mono small">
                          {/* The code leads to the record, as it does in every other
                              table of partner units. */}
                          {p.partner_id ? (
                            <Link to={`/partners/${p.partner_id}`}>{code(p.s4ca_code)}</Link>
                          ) : (
                            p.s4ca_code
                          )}
                        </td>
                        <td>{p.partner_name}</td>
                        <td className="num">{fmtInt(p.n)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty
                title={
                  picked
                    ? `No updates recorded for this country in ${picked}`
                    : 'No updates recorded for this country'
                }
              />
            )}
          </Card>

          <Card title="Activity Mix">
            {(() => {
              const buckets = d.partners.reduce((acc, p) => {
                acc[p.activity] = (acc[p.activity] || 0) + 1
                return acc
              }, {})
              const total = d.partners.length || 1
              return (
                <div className="col">
                  {['active', 'slowing', 'dormant', 'none'].map((k) => (
                    <div key={k}>
                      <div className="row small" style={{ marginBottom: 3 }}>
                        <ActivityBadge activity={k} />
                        <span className="spacer" />
                        <span className="nums">{fmtInt(buckets[k] ?? 0)}</span>
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${((buckets[k] ?? 0) / total) * 100}%`,
                            background:
                              k === 'active'
                                ? 'var(--ok)'
                                : k === 'slowing'
                                  ? 'var(--warn)'
                                  : k === 'dormant'
                                    ? 'var(--danger)'
                                    : 'var(--border-strong)',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </Card>
        </div>

        <Card title="Partner Units Registered in This Country" sub={`${fmtInt(d.partners.length)} records`}>
          {d.partners.length ? (
            <div className="table-wrap flush">
              <table className="data">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th className="right">Updates</th>
                    <th>Latest</th>
                    <th>Activity</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.partners.map((p) => (
                    <tr key={p.partner_id}>
                      <td>
                        <Link className="mono" to={`/partners/${p.partner_id}`}>
                          {code(p.s4ca_code)}
                        </Link>
                      </td>
                      <td>{p.display_name}</td>
                      <td className="num">{fmtInt(p.newsletter_count)}</td>
                      <td className="small nowrap">{p.last_newsletter ?? '—'}</td>
                      <td>
                        <ActivityBadge activity={p.activity} />
                      </td>
                      <td>
                        {p.newsletter_count > 0 && (
                          <Link
                            className="btn btn-sm"
                            to={`/reports/newsletters-by-partner?partner=${p.partner_id}`}
                          >
                            Report
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty title="No partner units registered here" />
          )}
        </Card>
      </div>
    </PageHeader>
  )
}
